import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CardActionArea,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { KeyText, StatusChip } from "./ui";
import { productName } from "../config";
import { formatDate, isDevelopment, licenseStatus } from "../lib/licenses";

export const DevTag = () => (
  <Chip
    label="Dev"
    size="small"
    variant="outlined"
    sx={{ height: 20, fontSize: 11, ml: 0.5 }}
  />
);

const Secondary = ({ children }) =>
  children ? (
    <Typography variant="caption" color="text.secondary" component="div" noWrap>
      {children}
    </Typography>
  ) : null;

const COLUMNS = {
  key: {
    label: "Key",
    render: (l) => (
      <Box sx={{ display: "inline-flex", alignItems: "center" }}>
        <KeyText value={l.licenseKey || l.id} />
        {isDevelopment(l) && <DevTag />}
      </Box>
    ),
  },
  status: {
    label: "Status",
    render: (l) => <StatusChip status={licenseStatus(l)} />,
  },
  type: { label: "Type", render: (l) => l.type || "—" },
  productType: {
    label: "Product",
    render: (l) => (
      <>
        {productName(l.product)}
        <Secondary>{l.type}</Secondary>
      </>
    ),
  },
  distributor: {
    label: "Distributor",
    render: (l) => (
      <>
        {l.distributor || "—"}
        <Secondary>{l.region}</Secondary>
      </>
    ),
  },
  customer: {
    label: "Customer",
    render: (l) => (
      <Box sx={{ maxWidth: 240 }}>
        <Typography variant="body2" noWrap>
          {l.customer || "—"}
        </Typography>
        <Secondary>{l.location}</Secondary>
      </Box>
    ),
  },
  created: { label: "Created", render: (l) => formatDate(l.createdOn) },
  issued: { label: "Issued", render: (l) => formatDate(l.issuedAt) },
  expiry: { label: "Expiry", render: (l) => l.expirydate || "—" },
};

function LicenseCardRow({ license, onClick, showDistributor }) {
  return (
    <Card sx={{ mb: 1.25 }}>
      <CardActionArea onClick={onClick} component="div">
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              <KeyText value={license.licenseKey || license.id} />
              {isDevelopment(license) && <DevTag />}
            </Box>
            <StatusChip status={licenseStatus(license)} />
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {license.customer || "No customer yet"}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {[
              `${productName(license.product)} · ${license.type || "—"}`,
              showDistributor ? license.distributor : null,
              license.expirydate ? `expires ${license.expirydate}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// Licence list: a table on desktop, cards on phones. `rows` are already
// filtered and sorted. Without `paging` the rows are paged here; with
// `paging` ({ page, pageSize, hasNext, hasPrev, onNext, onPrev, total }) they
// are one page of a server-side query (see usePagedQuery).
export default function LicenseTable({
  rows,
  columns,
  onRowClick,
  resetKey,
  emptyText = "No keys match.",
  initialRowsPerPage = 25,
  paging,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  useEffect(() => setPage(0), [resetKey]);

  const lastPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
  const current = Math.min(page, lastPage);
  const visible = paging
    ? rows
    : rows.slice(current * rowsPerPage, (current + 1) * rowsPerPage);

  const pagination = paging ? (
    <ServerPagination paging={paging} />
  ) : rows.length > rowsPerPage || rowsPerPage !== initialRowsPerPage ? (
    <TablePagination
      component="div"
      count={rows.length}
      page={current}
      onPageChange={(_, next) => setPage(next)}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[25, 50, 100]}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
    />
  ) : null;

  if (rows.length === 0 && !paging?.hasPrev) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 4, textAlign: "center", color: "text.secondary" }}
      >
        {emptyText}
      </Paper>
    );
  }

  if (isMobile) {
    return (
      <Box>
        {visible.map((license) => (
          <LicenseCardRow
            key={`${license.product}/${license.id}`}
            license={license}
            showDistributor={columns.includes("distributor")}
            onClick={() => onRowClick?.(license)}
          />
        ))}
        {pagination}
      </Box>
    );
  }

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small" sx={{ "& td, & th": { py: 1.1 } }}>
          <TableHead>
            <TableRow>
              {columns.map((name) => (
                <TableCell key={name}>{COLUMNS[name].label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((license) => (
              <TableRow
                key={`${license.product}/${license.id}`}
                hover
                onClick={() => onRowClick?.(license)}
                sx={{ cursor: onRowClick ? "pointer" : "default" }}
              >
                {columns.map((name) => (
                  <TableCell key={name}>
                    {COLUMNS[name].render(license)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </Paper>
  );
}

export function ServerPagination({ paging }) {
  if (!paging.hasNext && !paging.hasPrev) return null;
  return (
    <TablePagination
      component="div"
      count={paging.total ?? -1}
      page={paging.page}
      rowsPerPage={paging.pageSize}
      rowsPerPageOptions={[]}
      onPageChange={(_, next) =>
        next > paging.page ? paging.onNext() : paging.onPrev()
      }
      slotProps={{
        actions: {
          nextButton: { disabled: !paging.hasNext },
          previousButton: { disabled: !paging.hasPrev },
        },
      }}
    />
  );
}

// Props for LicenseTable/CustomerTable from a usePagedQuery result.
export const pagingProps = (paged, pageSize, total) => ({
  page: paged.page,
  pageSize,
  hasNext: paged.hasNext,
  hasPrev: paged.hasPrev,
  onNext: paged.next,
  onPrev: paged.prev,
  total,
});
